/**
 * MvbDeclarationDetail -- single-declaration view + sign-off ceremony (P7.4).
 *
 * Reads the declaration via /v1/admin/foundation/mvb-declarations/{id};
 * renders status-aware actions (submit on draft; sign-off CRO + AE on
 * open; locked on approved/superseded).
 */
import { canRunOperations, canSignAs } from "shared/api/authority";
import { refusalMessage } from "shared/api/refusalMessage";
import { useMe } from "shared/api/useMe";
import { formatDate, formatDateTime } from "shared/time/formatDate";
import { useSeatHolders } from "shared/api/useSeatHolders";
import { i18n } from "shared/i18n";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { stateLabel } from "shared/vocab/workflowState";
import { useToast } from "shared/toast/ToastProvider";

import {
  useDeleteMvbDeclaration,
  useMvbDeclaration,
  useMvbDeclarations,
  useMvbDeclarationPreview,
  useSignOffMvbDeclaration,
  useSubmitMvbDeclaration,
  useWithdrawMvbSignature,
} from "foundation/api/hooks";
import { ReviewAndSign } from "shared/components/ReviewAndSign";
import type { PriorSignature } from "shared/components/ReviewAndSign";
import type {
  MeOut,
  MvbDeclaration,
  MvbSignoffScope,
  MvbStatus,
} from "shared/api/types";

import { Arrow } from "shared/components/Arrow";
import { SignatureHistory } from "shared/components/SignatureHistory";
import { Bdi } from "shared/components/Bdi";
import { membersSentence } from "shared/mvb/membersNow";
/**
 * A2 -- the same four lines /my-work puts in front of a signer.
 *
 * Kept deliberately in step with `_declaration_summary` in
 * backend/dossier/my_work.py: a signer who reads one summary in the
 * inbox and a different one on the record has been shown two versions
 * of what they signed, which is worse than showing none.
 */
function declarationSummary(d: MvbDeclaration): { label_key: string; value: string }[] {
  return [
    { label_key: "myWork.summary.proposal", value: d.name },
    {
      label_key: "myWork.summary.rationale",
      value: d.rationale ?? d.description ?? "",
    },
    { label_key: "myWork.summary.effectiveFrom", value: d.effective_from ?? "" },
    {
      // Round 9 C5. "MVB threshold 48" with no unit has been read as
      // days. The signing panel already uses the label that names
      // hours; the draft -- the page the submitter actually assembles
      // it on -- did not.
      label_key: "myWork.summary.thresholdHours",
      value: String(d.mvb_threshold_hours),
    },
  ].filter((line) => line.value !== "");
}

/**
 * A2 -- this page signed on ONE CLICK, with nothing on screen saying
 * what the signature committed the bank to. The inbox had been fixed
 * (B1) and the record page had not, so the safer route was the one
 * through the console. This is the inbox's own panel, not a second
 * one written to look like it.
 */
/**
 * Round 11 F1 -- the signature already on the declaration.
 *
 * Only the CRO's leg can precede a signature here: the AE signs last
 * and the panel is gone by then. Built from the record's own stored
 * columns for the same reason the inbox does -- the name as it stood
 * when the signature was given, not whoever holds the seat now.
 */
function priorSignatures(decl: MvbDeclaration): PriorSignature[] {
  if (!decl.cro_signed_at && !decl.cro_signoff_name) return [];
  return [
    {
      role: "cro",
      name: decl.cro_signoff_name ?? null,
      at: decl.cro_signed_at,
      note: decl.cro_signoff_note ?? null,
    },
  ];
}

function SignBlock({
  decl,
  role,
  signedAt,
  label,
  disabled,
  pending,
  onConfirm,
}: {
  decl: MvbDeclaration;
  role: "cro" | "ae";
  signedAt: string | null;
  label: string;
  disabled: boolean;
  pending: boolean;
  onConfirm: (note: string | null) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Round 9 C5. Neither sign leg took a comment, so the finished record
  // read "Signed without a comment" under three names when no box had
  // ever been offered -- which reads as three people declining to
  // explain themselves rather than as the product never asking.
  const [note, setNote] = useState("");
  const summary = declarationSummary(decl);

  if (signedAt) return null;
  if (!open) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        data-testid={`mvb-${role}-signoff-btn`}
        className="mt-2 rounded bg-slate-900 px-2 py-1 text-xs text-white disabled:bg-slate-300"
      >
        {label}
      </button>
    );
  }
  return (
    <div className="mt-2">
      <ReviewAndSign
        testid={`mvb-${role}`}
        title={decl.name}
        summary={summary}
        /* Round 11 F1 -- the AE signing second sees the CRO's
           signature, named and dated, on the page as well as in the
           inbox. The record already held all three facts. */
        signedSoFar={priorSignatures(decl)}
        commitsToKey="myWork.commits.mvbDeclaration"
        recordLink={`/foundation/mvb-declarations/${decl.id}`}
        pending={pending}
        confirmLabel={pending ? t("myWork.action.working") : label}
        onCancel={() => setOpen(false)}
        onConfirm={() => onConfirm(note.trim() || null)}
      >
        <label className="block text-xs text-slate-600">
          {t("mvbDeclarationDetail.signNote")}
          <textarea
            data-testid={`mvb-${role}-signoff-note`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-xs"
          />
        </label>
      </ReviewAndSign>
    </div>
  );
}

/**
 * Round 14 C5 -- can this viewer take back the signature on `role`?
 *
 * The server admits the signer or an administrator. /v1/me carries no
 * local user id, so the signer is matched by the name the record
 * stamped at signing -- the same name the signatures block prints. A
 * miss hides the control; the server stays the precise gate.
 */
function canWithdraw(
  me: MeOut | undefined,
  decl: MvbDeclaration,
  role: "cro" | "ae",
): boolean {
  if (decl.status !== "open") return false;
  const signedAt = role === "cro" ? decl.cro_signed_at : decl.ae_signed_at;
  if (!signedAt || !me) return false;
  if (me.is_admin) return true;
  const signer = role === "cro" ? decl.cro_signoff_name : decl.ae_signoff_name;
  return !!signer && signer === (me.display_name || me.username);
}

/**
 * Round 14 C6 (P3) -- the set the first signature stands over, when it
 * is no longer the set a second signature would put in force.
 *
 * The AE met this only as a refusal after pressing. The preview carries
 * the current set's fingerprint, so the page can say it first.
 */
function movedFrom(
  decl: MvbDeclaration,
  now: MvbSignoffScope | undefined,
): { by: "cro" | "ae"; signed: MvbSignoffScope } | null {
  if (decl.status !== "open" || !now) return null;
  const cro = decl.cro_signed_at ? decl.cro_signoff_scope : null;
  const ae = decl.ae_signed_at ? decl.ae_signoff_scope : null;
  if (cro && !ae && cro.digest !== now.digest) return { by: "cro", signed: cro };
  if (ae && !cro && ae.digest !== now.digest) return { by: "ae", signed: ae };
  return null;
}

const STATUS_TONE: Record<MvbStatus, string> = {
  draft: "bg-slate-100 text-slate-700",
  open: "bg-amber-100 text-amber-900",
  approved: "bg-emerald-100 text-emerald-900",
  superseded: "bg-slate-200 text-slate-500",
};

/** Today as YYYY-MM-DD in the reader's zone, to compare a start date. */
function todayIso(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

export function MvbDeclarationDetail() {
  const { t } = useTranslation();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();
  const query = useMvbDeclaration(id);
  // Round 16 G12 -- who holds each seat, so the waiting line names a
  // person to chase rather than a role.
  const seats = useSeatHolders();
  const seatWho = (seat: "cro" | "ae", label: string): string | null => {
    const holders = seats.data?.holders?.[seat];
    return holders && holders.length === 1 ? `${holders[0]} (${label})` : null;
  };
  // Round 14 C2 -- the register, to name what replaced this declaration
  // and what it replaced. Both records say so.
  const register = useMvbDeclarations();
  // Only fetched while it can still change; an approved declaration
  // carries its own frozen snapshot and needs no preview of one.
  const preview = useMvbDeclarationPreview(id, query.data?.status !== "approved");
  const submit = useSubmitMvbDeclaration();
  const signOff = useSignOffMvbDeclaration();
  const withdraw = useWithdrawMvbSignature();
  const remove = useDeleteMvbDeclaration();
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  // D6 -- the reason the set is being put forward, which the record has
  // had a column for all along. Declared with the other hooks, above
  // the loading/error returns: hooks run in the same order every time.
  const [submitNote, setSubmitNote] = useState("");
  // A2: a signature is the signer's own, so the buttons appear only for
  // the session that can actually give one. Everyone else is told who
  // is being waited on rather than being offered a refusal. Read before
  // the loading/error returns -- hooks run in the same order every time.
  const me = useMe();
  const canSignCro = canSignAs(me.data, "cro");
  const canSignAe = canSignAs(me.data, "ae");

  if (query.isLoading) {
    return (
      <div data-testid="mvb-detail-loading" className="text-sm text-slate-600">
        {t("mvbDeclarationDetail.loading")}
      </div>
    );
  }
  if (query.error) {
    return (
      <div
        data-testid="mvb-detail-error"
        className="rounded bg-rose-50 p-3 text-sm text-rose-900"
      >
        {t("mvbDeclarationDetail.loadError", { message: query.error.message })}
      </div>
    );
  }
  const decl = query.data;
  if (!decl) return null;
  // Round 18 L10 -- the frozen set's members by class and admission.
  const membersLine = membersSentence(decl.members_now, t);

  // Shape-guarded: a partial payload must not take the record down
  // with it (the P76/P362 class).
  const registerRows = Array.isArray(register.data) ? register.data : [];
  const replacedBy = registerRows.find((d) => d.id === decl.superseded_by_id);
  const replaces = registerRows.filter((d) => d.superseded_by_id === decl.id);
  const canSubmit = decl.status === "draft";
  const canSignOff = decl.status === "open";
  const canDelete =
    (decl.status === "draft" || decl.status === "open") && canRunOperations(me.data);
  const rawScope = (preview.data as { scope?: MvbSignoffScope } | undefined)?.scope;
  const nowScope = typeof rawScope?.digest === "string" ? rawScope : undefined;
  const moved = movedFrom(decl, nowScope);
  // Round 14 C6 -- every refusal this page can meet, in the reader's
  // language. The sign-off refusals rendered nowhere at all.
  const refused = [signOff, withdraw, remove, submit].find((m) => m.isError)?.error;
  const withdrawButton = (role: "cro" | "ae") =>
    canWithdraw(me.data, decl, role) ? (
      <button
        type="button"
        disabled={withdraw.isPending}
        onClick={() =>
          withdraw.mutate(
            { id: decl.id, role },
            {
              onSuccess: () =>
                toast.push({
                  tone: "success",
                  message: t("mvbDeclarationDetail.withdrawnToast"),
                }),
            },
          )
        }
        data-testid={`mvb-${role}-withdraw-btn`}
        className="mt-1 text-xs text-brand-brassDark underline decoration-dotted underline-offset-2 disabled:text-slate-400"
      >
        {t("mvbDeclarationDetail.withdrawSignature")}
      </button>
    ) : null;
  const movedNotice = (role: "cro" | "ae") =>
    moved && moved.by !== role ? (
      <p
        data-testid={`mvb-${role}-set-moved`}
        className="mt-1 rounded bg-amber-50 p-2 text-xs text-amber-900"
      >
        {t("mvbDeclarationDetail.setMoved", {
          role:
            moved.by === "cro"
              ? t("mvbDeclarationDetail.cro")
              : t("mvbDeclarationDetail.ae"),
          signedIbs: moved.signed.ibs_count,
          signedServices: moved.signed.service_count,
          signedVendors: moved.signed.vendor_count,
          nowIbs: nowScope?.ibs_count ?? 0,
          nowServices: nowScope?.service_count ?? 0,
          nowVendors: nowScope?.vendor_count ?? 0,
        })}
      </p>
    ) : null;

  return (
    <article
      data-testid="mvb-detail"
      data-status={decl.status}
      data-declaration-id={decl.id}
      className="space-y-4"
    >
      <header className="space-y-2">
        <div className="flex items-baseline justify-between gap-3">
          <h1 className="text-2xl font-semibold text-slate-900">{decl.name}</h1>
          <span
            data-testid="mvb-detail-status"
            className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_TONE[decl.status]}`}
          >
            {stateLabel(decl.status)}
          </span>
        </div>
        {decl.description && (
          <p className="text-sm text-slate-700">{decl.description}</p>
        )}
        {decl.rationale && (
          <p className="text-sm text-slate-600">
            <span className="font-medium">{t("mvbDeclarationDetail.rationale")}</span>{" "}
            {decl.rationale}
          </p>
        )}
      </header>

      <section className="rounded border border-slate-200 bg-white p-3 text-sm space-y-1">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t("mvbDeclarationDetail.effectiveWindow")}
        </h2>
        {/* Round 9 C5: on a draft both ends are empty, so this read
            "— → open-ended", which describes nothing. A draft has no
            window because it is not in force yet, and saying that is
            more use than punctuation. */}
        {decl.effective_from ? (
          <p>
            {decl.effective_from} <Arrow />{" "}
            {decl.effective_to ?? t("mvbDeclarationDetail.openEnded")}
          </p>
        ) : (
          <p data-testid="mvb-window-not-yet" className="text-slate-600">
            {t("mvbDeclarationDetail.windowOnApproval")}
          </p>
        )}
        {/* Round 16 H21, sixth round -- the record holds the bank's
            Gate-1 threshold and printed it only inside the signing
            panel, so the declaration in force did not say it. */}
        <p data-testid="mvb-detail-threshold" className="text-slate-700">
          {t("mvbDeclarationDetail.threshold", { hours: decl.mvb_threshold_hours })}
        </p>
      </section>

      <section className="rounded border border-slate-200 bg-white p-3 text-sm space-y-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t("mvbDeclarationDetail.ceremony")}
        </h2>
        {/* C1 -- the same signatures block the admission register got.
            The declaration collected both signatures and both comments
            and showed neither: two cells reading "Signed 3 Sep" with no
            person and no reason, on the record that puts the bank's
            minimum viable set in force. The data was never the
            obstacle. */}
        <SignatureHistory
          testid="mvb-declaration-signatures"
          entries={[
            // C2 -- the submission is the ceremony's first leg and the
            // only one that could not carry a reason.
            {
              roleLabel: t("roles.submitter"),
              name: decl.submitter_name ?? null,
              at: decl.submitted_at,
              note: decl.submitted_note ?? null,
            },
            {
              roleLabel: t("roles.cro"),
              name: decl.cro_signoff_name ?? null,
              at: decl.cro_signed_at,
              note: decl.cro_signoff_note ?? null,
            },
            {
              roleLabel: t("roles.ae"),
              name: decl.ae_signoff_name ?? null,
              at: decl.ae_signed_at,
              note: decl.ae_signoff_note ?? null,
            },
          ]}
        />
        {/* Round 20 B32 -- a signature given by someone not holding the
            seat does not count, and an effective date before the last
            signature is said. */}
        {Object.entries(decl.signer_seats ?? {})
          .filter(([, s]) => !s.holds_seat)
          .map(([leg, s]) => (
            <p
              key={leg}
              data-testid={`mvb-signer-unheld-${leg}`}
              className="rounded bg-rose-50 px-2 py-1 text-xs text-rose-800"
            >
              {t("mvbDeclarationDetail.signerUnheld", {
                name:
                  (leg === "cro" ? decl.cro_signoff_name : decl.ae_signoff_name) ??
                  t("mvbDeclarationDetail.unnamedSigner"),
                seat: t(`roles.${leg}`),
                atSigning: s.seat_at_signing
                  ? t(`roles.${s.seat_at_signing}`, { defaultValue: s.seat_at_signing })
                  : t("mvbDeclarationDetail.seatNotRecorded"),
              })}
            </p>
          ))}
        {decl.review_before_effective && (
          <p
            data-testid="mvb-review-before-effective"
            className="rounded bg-amber-50 px-2 py-1 text-xs text-amber-900"
          >
            {t("mvbDeclarationDetail.reviewBeforeEffective", {
              review: formatDate(decl.review_due_at, i18n.language),
              effective: formatDate(decl.effective_from, i18n.language),
            })}
          </p>
        )}
        {decl.effective_before_signature && (
          <p
            data-testid="mvb-effective-before-signature"
            className="rounded bg-amber-50 px-2 py-1 text-xs text-amber-900"
          >
            {t("mvbDeclarationDetail.effectiveBeforeSignature", {
              when: formatDate(decl.effective_from, i18n.language),
            })}
          </p>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div data-testid="mvb-cro-block">
            <p className="text-xs text-slate-500">{t("mvbDeclarationDetail.cro")}</p>
            {decl.cro_signed_at ? (
              <p className="text-sm text-emerald-900 tabular-nums">
                {t("mvbDeclarationDetail.signed", {
                  when: formatDateTime(decl.cro_signed_at, i18n.language),
                })}
              </p>
            ) : (
              <p className="text-sm text-slate-600">
                {t("mvbDeclarationDetail.notSigned")}
              </p>
            )}
            {withdrawButton("cro")}
            {canSignCro && !decl.cro_signed_at && movedNotice("cro")}
            {canSignCro && (
              <SignBlock
                decl={decl}
                role="cro"
                signedAt={decl.cro_signed_at}
                label={t("mvbDeclarationDetail.signCro")}
                disabled={!canSignOff || signOff.isPending || !!moved}
                pending={signOff.isPending}
                onConfirm={(note) => signOff.mutate({ id: decl.id, role: "cro", note })}
              />
            )}
          </div>
          <div data-testid="mvb-ae-block">
            <p className="text-xs text-slate-500">{t("mvbDeclarationDetail.ae")}</p>
            {decl.ae_signed_at ? (
              <p className="text-sm text-emerald-900 tabular-nums">
                {t("mvbDeclarationDetail.signed", {
                  when: formatDateTime(decl.ae_signed_at, i18n.language),
                })}
              </p>
            ) : (
              <p className="text-sm text-slate-600">
                {t("mvbDeclarationDetail.notSigned")}
              </p>
            )}
            {withdrawButton("ae")}
            {/* Round 17 G16 -- the declaration comes to the AE after the
                CRO, as his home teaches; until then he is told who signs
                first, and offered nothing. */}
            {canSignAe && !decl.ae_signed_at && !decl.cro_signed_at && (
              <p data-testid="mvb-ae-waits-on-cro" className="text-xs text-slate-600">
                {t("mvbDeclarationDetail.aeAfterCro", {
                  who: seatWho("cro", t("roles.cro")) ?? t("roles.cro"),
                })}
              </p>
            )}
            {canSignAe && !decl.ae_signed_at && decl.cro_signed_at && movedNotice("ae")}
            {canSignAe && (decl.ae_signed_at || decl.cro_signed_at) && (
              <SignBlock
                decl={decl}
                role="ae"
                signedAt={decl.ae_signed_at}
                label={t("mvbDeclarationDetail.signAe")}
                disabled={!canSignOff || signOff.isPending || !!moved}
                pending={signOff.isPending}
                onConfirm={(note) => signOff.mutate({ id: decl.id, role: "ae", note })}
              />
            )}
          </div>
        </div>
        {/* A3 -- what the RECORD is waiting for, not what the reader
            cannot do.
            This rendered under !canSignCro && !canSignAe, which is true
            of anybody who cannot sign -- including somebody reading a
            finished ceremony. So a declaration carrying both signatures
            and both timestamps, three lines above, still said "Waiting
            on the CRO and the accountable executive". That is C3's
            shape on the one record the bank's two most senior
            signatories put their names to. */}
        {replaces.length > 0 && (
          <p data-testid="mvb-declaration-replaces" className="text-xs text-slate-600">
            {t("mvbDeclarationDetail.replaces")}{" "}
            {replaces.map((r, i) => (
              <span key={r.id}>
                {i > 0 && ", "}
                <Link
                  to={`/foundation/mvb/${r.id}`}
                  className="text-brand-brassDark underline decoration-dotted underline-offset-2"
                >
                  <Bdi>{r.name}</Bdi>
                </Link>
              </span>
            ))}
          </p>
        )}
        {/* Round 14 C2 -- a superseded declaration said "In force since"
            beside the one that replaced it, and a declaration dated to
            start next week said it had been in force since then. */}
        {decl.status === "superseded" ? (
          <p
            data-testid="mvb-declaration-superseded"
            className="text-xs text-slate-600"
          >
            {replacedBy ? (
              <>
                {t("mvbDeclarationDetail.supersededBy", {
                  when: decl.effective_to
                    ? formatDate(decl.effective_to, i18n.language)
                    : "--",
                })}{" "}
                <Link
                  to={`/foundation/mvb/${replacedBy.id}`}
                  className="text-brand-brassDark underline decoration-dotted underline-offset-2"
                >
                  <Bdi>{replacedBy.name}</Bdi>
                </Link>
              </>
            ) : (
              t("mvbDeclarationDetail.supersededUnnamed")
            )}
          </p>
        ) : decl.status === "approved" &&
          decl.effective_from &&
          decl.effective_from > todayIso() ? (
          <p
            data-testid="mvb-declaration-not-yet-in-force"
            className="text-xs text-slate-700"
          >
            {t("mvbDeclarationDetail.comesIntoForce", {
              when: formatDate(decl.effective_from, i18n.language),
              cro: decl.cro_signoff_name ?? t("mvbDeclarationDetail.cro"),
              ae: decl.ae_signoff_name ?? t("mvbDeclarationDetail.ae"),
            })}
          </p>
        ) : decl.cro_signed_at && decl.ae_signed_at ? (
          <p data-testid="mvb-declaration-in-force" className="text-xs text-rag-green">
            {t("mvbDeclarationDetail.inForceSignedBy", {
              when: decl.effective_from
                ? formatDate(decl.effective_from, i18n.language)
                : formatDate(decl.ae_signed_at, i18n.language),
              cro: decl.cro_signoff_name ?? t("mvbDeclarationDetail.cro"),
              ae: decl.ae_signoff_name ?? t("mvbDeclarationDetail.ae"),
            })}
          </p>
        ) : decl.cro_signed_at || decl.ae_signed_at ? (
          <p
            data-testid="mvb-declaration-signoff-waiting"
            className="text-xs text-slate-600"
          >
            {/* One signature in: name the one still outstanding, rather
                than both seats regardless of how far it has got. */}
            {(() => {
              const role = decl.cro_signed_at
                ? t("mvbDeclarationDetail.ae")
                : t("mvbDeclarationDetail.cro");
              const who = seatWho(decl.cro_signed_at ? "ae" : "cro", role);
              return who
                ? t("mvbDeclarationDetail.waitingOnOneNamed", { who })
                : t("mvbDeclarationDetail.waitingOnOne", { role });
            })()}
          </p>
        ) : decl.status === "open" && !canSignCro && !canSignAe ? (
          /* Round 16 G12 -- nothing waits on a signer before the draft
             is submitted; the line stood over unsubmitted drafts. */
          <p
            data-testid="mvb-declaration-signoff-waiting"
            className="text-xs text-slate-600"
          >
            {(() => {
              const cro = seatWho("cro", t("mvbDeclarationDetail.cro"));
              const ae = seatWho("ae", t("mvbDeclarationDetail.ae"));
              return cro && ae
                ? t("mvbDeclarationDetail.waitingOnNamedSigners", { cro, ae })
                : t("mvbDeclarationDetail.waitingOnSigners");
            })()}
          </p>
        ) : null}
      </section>

      {refused && (
        <p
          role="alert"
          data-testid="mvb-detail-refusal"
          className="rounded bg-rose-50 p-2 text-sm text-rose-900"
        >
          {refusalMessage(t, refused)}
        </p>
      )}

      {canSubmit && (
        <div className="space-y-1">
          {/* D6 (round 8) -- the box the endpoint has been keeping.
              `MvbDeclarationSubmitIn.note` and `submitted_note` both
              exist and nothing ever asked for one, so every submission
              recorded an absence -- which the signatures block then
              renders, correctly, as "Signed without a comment". A step
              that never asked reads as a signer who declined. */}
          <label
            htmlFor="mvb-submit-note"
            className="block text-xs font-medium text-slate-700"
          >
            {t("mvbDeclarationDetail.submitNoteLabel")}
          </label>
          <textarea
            id="mvb-submit-note"
            data-testid="mvb-submit-note"
            dir="auto"
            rows={2}
            value={submitNote}
            onChange={(e) => setSubmitNote(e.target.value)}
            placeholder={t("mvbDeclarationDetail.submitNotePlaceholder")}
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
          <button
            type="button"
            onClick={() =>
              submit.mutate(
                { id: decl.id, note: submitNote.trim() || null },
                {
                  // D9 -- it submitted silently. Submitting hands the
                  // declaration to the CRO, and the person who sent it
                  // was told neither that it had gone nor to whom.
                  onSuccess: () =>
                    toast.push({
                      tone: "success",
                      message: t("mvbDeclarationDetail.submittedToast"),
                    }),
                },
              )
            }
            disabled={submit.isPending}
            data-testid="mvb-submit-btn"
            className="rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white disabled:bg-slate-400"
          >
            {t("mvbDeclarationDetail.submit")}
          </button>
        </div>
      )}

      {/* B3 -- what she is being asked to put in force, BEFORE she
          signs. The draft showed a name, a state, an empty effective
          window and a bare threshold number; the membership appeared
          only once the declaration was approved, which is one signature
          too late. Same composer as the freeze, so this cannot promise
          something the freeze then does differently. */}
      {/* Round 16 G14 -- a superseded declaration was headed "What
          signing this would put in force": nobody can sign it, and what
          it put in force is its own frozen set, shown below. */}
      {(decl.status === "draft" || decl.status === "open") && (
        <section
          data-testid="mvb-declaration-preview"
          className="rounded border border-paper-2 bg-white p-3 text-sm"
        >
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t("mvbDeclarationDetail.previewHeading")}
          </h2>
          {preview.isLoading ? (
            <p className="mt-1 text-xs text-slate-500">
              {t("mvbDeclarationDetail.previewLoading")}
            </p>
          ) : preview.isError ? (
            <p className="mt-1 text-xs text-slate-500">
              {t("mvbDeclarationDetail.previewUnavailable")}
            </p>
          ) : (
            (() => {
              const ibses = Array.isArray(preview.data?.ibs_services)
                ? (preview.data.ibs_services as {
                    name?: string;
                    code?: string;
                    service_class?: string | null;
                    admission_status?: string | null;
                  }[])
                : [];
              if (!ibses.length) {
                // An empty set is stated, not left blank: signing this
                // would put NOTHING in force, and that is the single
                // most important thing the page can say here.
                return (
                  <p
                    data-testid="mvb-declaration-preview-empty"
                    className="mt-1 text-xs text-rag-amber"
                  >
                    {t("mvbDeclarationDetail.previewEmpty")}
                  </p>
                );
              }
              return (
                <>
                  <p className="mt-1 text-xs text-slate-600">
                    {t("mvbDeclarationDetail.previewCount", { count: ibses.length })}
                    {ibses.some((i) => i.service_class === "internal") &&
                      t("mvbDeclarationDetail.previewFunctions", {
                        count: ibses.filter((i) => i.service_class === "internal")
                          .length,
                      })}
                  </p>
                  <ul className="mt-1 space-y-0.5 text-xs text-slate-800">
                    {ibses.map((i, n) => (
                      <li key={i.code ?? String(n)}>
                        <Bdi>{i.name ?? i.code ?? ""}</Bdi>
                        <MemberNote m={i} />
                      </li>
                    ))}
                  </ul>
                </>
              );
            })()
          )}
        </section>
      )}

      {/* Round 9 F1 -- what she does NEXT with a set she has just put
          in force.
          Three of the ten Twin screens no persona has opened in nine
          rounds are about this declaration: the order its services come
          back in, whether their continuity posture is sufficient for
          the tolerances she just signed, and the drill that tests both.
          The declaration led to none of them. Signing was the end of
          the journey rather than the start of one. */}
      {decl.status === "approved" && (
        <section
          data-testid="mvb-declaration-next"
          className="rounded-xl border border-brand-brass/40 bg-brand-brass/5 p-3 text-sm"
        >
          <h2 className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-brand-brassDark">
            {t("mvbDeclarationDetail.nowInForce")}
          </h2>
          <p className="mt-1 text-xs text-ink-2">
            {t("mvbDeclarationDetail.nowInForceBody")}
          </p>
          <ul className="mt-2 space-y-0.5 text-xs">
            <li>
              <Link
                to="/twin/mvb-recovery"
                data-testid="mvb-declaration-to-recovery"
                className="text-brand-brassDark hover:underline"
              >
                {t("mvbDeclarationDetail.toRecovery")}
              </Link>
            </li>
            <li>
              <Link
                to="/twin/continuity-posture"
                data-testid="mvb-declaration-to-posture"
                className="text-brand-brassDark hover:underline"
              >
                {t("mvbDeclarationDetail.toPosture")}
              </Link>
            </li>
            <li>
              <Link
                to="/twin/mvb-drill"
                data-testid="mvb-declaration-to-drill"
                className="text-brand-brassDark hover:underline"
              >
                {t("mvbDeclarationDetail.toDrill")}
              </Link>
            </li>
          </ul>
        </section>
      )}

      {(decl.status === "approved" || decl.status === "superseded") &&
        decl.snapshot && (
          <section
            data-testid="mvb-snapshot-summary"
            className="rounded border border-emerald-200 bg-emerald-50 p-3 text-sm"
          >
            <h2 className="text-xs font-semibold uppercase tracking-wide text-emerald-900">
              {t("mvbDeclarationDetail.frozenSnapshot")}
            </h2>
            {/* Round 18 L10 -- the members by class and admission, read
                from the register today. */}
            {membersLine && (
              <p
                data-testid="mvb-snapshot-members-sentence"
                className="mt-1 text-xs text-emerald-900"
              >
                {membersLine}{" "}
                <span className="text-slate-500">
                  ({t("mvbMembers.readFromRegister")})
                </span>
              </p>
            )}
            <dl className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
              {!membersLine && (
                <>
                  {/* Round 17 L6 -- "IBSes 6" counted Treasury settlement. */}
                  <dt className="text-slate-600">{t("mvbDeclarationDetail.ibses")}</dt>
                  <dd className="tabular-nums" data-testid="mvb-snapshot-ibs-count">
                    {
                      (
                        (decl.snapshot.ibs_services as
                          { service_class?: string | null }[] | undefined) ?? []
                      ).filter((i) => i.service_class !== "internal").length
                    }
                  </dd>
                  {(
                    (decl.snapshot.ibs_services as
                      { service_class?: string | null }[] | undefined) ?? []
                  ).some((i) => i.service_class === "internal") && (
                    <>
                      <dt className="text-slate-600">
                        {t("mvbDeclarationDetail.functions")}
                      </dt>
                      <dd
                        className="tabular-nums"
                        data-testid="mvb-snapshot-function-count"
                      >
                        {
                          (
                            (decl.snapshot.ibs_services as {
                              service_class?: string | null;
                            }[]) ?? []
                          ).filter((i) => i.service_class === "internal").length
                        }
                      </dd>
                    </>
                  )}
                </>
              )}
              <dt className="text-slate-600">{t("mvbDeclarationDetail.services")}</dt>
              <dd className="tabular-nums">
                {(decl.snapshot.services as unknown[] | undefined)?.length ?? 0}
              </dd>
              <dt className="text-slate-600">{t("mvbDeclarationDetail.vendors")}</dt>
              <dd className="tabular-nums">
                {(decl.snapshot.vendors as unknown[] | undefined)?.length ?? 0}
              </dd>
            </dl>
            {/* B1 -- counts with no names, on the record whose stated
              purpose is what the board and the regulator will be shown.
              The six names were on screen while it was a draft and were
              replaced by "IBSes 6" the moment it was put in force. The
              snapshot has always held them; nothing rendered them. */}
            {(() => {
              const frozen = Array.isArray(decl.snapshot?.ibs_services)
                ? (decl.snapshot.ibs_services as {
                    id?: string;
                    name?: string;
                    code?: string;
                    service_class?: string | null;
                    admission_status?: string | null;
                  }[])
                : [];
              if (!frozen.length) return null;
              return (
                <ul
                  data-testid="mvb-snapshot-members"
                  className="mt-2 space-y-0.5 border-t border-emerald-200 pt-2 text-xs text-emerald-900"
                >
                  {frozen.map((i, n) => (
                    <li key={i.code ?? String(n)}>
                      <Bdi>{i.name ?? i.code ?? ""}</Bdi>
                      {/* Round 18 L10 -- a snapshot frozen before round 17
                          carries no class; the register says it today. */}
                      <MemberNote
                        m={{
                          ...(i.id ? decl.members_now?.by_id?.[i.id] : undefined),
                          ...(i.service_class
                            ? { service_class: i.service_class }
                            : {}),
                          ...(i.admission_status
                            ? { admission_status: i.admission_status }
                            : {}),
                        }}
                      />
                    </li>
                  ))}
                </ul>
              );
            })()}
          </section>
        )}
      {/* Round 14 C5 -- the refusals said "delete it" and no screen
          offered the verb. A declaration never in force can be removed
          by whoever may start one; one that has been in force is
          superseded, and the control is not offered. */}
      {canDelete && (
        <section
          data-testid="mvb-declaration-delete"
          className="rounded border border-slate-200 bg-white p-3 text-sm"
        >
          {!confirmDelete ? (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              data-testid="mvb-delete-btn"
              className="text-xs text-rose-800 underline decoration-dotted underline-offset-2"
            >
              {t("mvbDeclarationDetail.deleteDeclaration")}
            </button>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-slate-700">
                {t("mvbDeclarationDetail.deleteConfirmBody", { name: decl.name })}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={remove.isPending}
                  onClick={() =>
                    remove.mutate(
                      { id: decl.id },
                      {
                        onSuccess: () => {
                          toast.push({
                            tone: "success",
                            message: t("mvbDeclarationDetail.deletedToast", {
                              name: decl.name,
                            }),
                          });
                          navigate("/foundation/mvb");
                        },
                      },
                    )
                  }
                  data-testid="mvb-delete-confirm"
                  className="rounded bg-rose-700 px-2 py-1 text-xs text-white disabled:bg-slate-300"
                >
                  {t("mvbDeclarationDetail.deleteConfirm")}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  data-testid="mvb-delete-cancel"
                  className="rounded border border-slate-300 px-2 py-1 text-xs"
                >
                  {t("mvbDeclarationDetail.deleteCancel")}
                </button>
              </div>
            </div>
          )}
        </section>
      )}
    </article>
  );
}

/** Round 17 L6 -- what a member is, where the record says. Older
 *  snapshots carry no class and read as before. */
function MemberNote({
  m,
}: {
  m: { service_class?: string | null; admission_status?: string | null };
}): JSX.Element | null {
  const { t } = useTranslation();
  const notes: string[] = [];
  if (m.service_class === "internal")
    notes.push(t("mvbDeclarationDetail.memberFunction"));
  if (m.admission_status && m.admission_status !== "admitted")
    notes.push(t("mvbDeclarationDetail.memberNotAdmitted"));
  if (!notes.length) return null;
  return (
    /* Round 19 L15 -- a space in the text, not a margin: it read
       "Treasury settlement(critical internal function)" when copied or
       read aloud. */
    <span data-testid="mvb-member-note" className="text-slate-500">
      {" "}
      ({notes.join(", ")})
    </span>
  );
}
